import { Fragment, useState } from 'react';
import type { PolicyDoc, PolicyStatus, PolicyToken } from '@wedpo/soc2';
import { policyTemplates } from '@wedpo/soc2';
import { ControlChips } from '@/components/common';

type TokenValues = Record<string, Record<string, string>>;
type StatusOverrides = Record<string, PolicyStatus>;

const STATUS_LABEL: Record<PolicyStatus, string> = {
  adopted: 'Adopted',
  customizing: 'Customizing',
  'not-started': 'Not started',
};

export function Policies({
  tokenValues,
  setTokenValue,
  statusOverrides,
  adopt,
}: {
  tokenValues: TokenValues;
  setTokenValue: (policyId: string, tokenId: string, value: string) => void;
  statusOverrides: StatusOverrides;
  adopt: (policyId: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const policies = policyTemplates.policies;

  const valOf = (pid: string, t: PolicyToken): string =>
    tokenValues[pid]?.[t.id] ?? (t.value ?? '');
  const statusOf = (p: PolicyDoc): PolicyStatus => statusOverrides[p.id] ?? p.status;

  const open = openId ? policies.find((p) => p.id === openId) ?? null : null;

  if (open) {
    return (
      <PolicyEditor
        policy={open}
        valOf={(t) => valOf(open.id, t)}
        onChange={(tid, v) => setTokenValue(open.id, tid, v)}
        onBack={() => setOpenId(null)}
        onAdopt={() => {
          adopt(open.id);
          setOpenId(null);
        }}
      />
    );
  }

  return (
    <section>
      <p className="eyebrow">Documents · policy library</p>
      <h1 className="title">Policies</h1>
      <p className="sub">
        Pre-written templates mapped to your controls. Customize the highlighted spots -
        each one tells you exactly what it needs - then adopt. Adopting a policy marks its
        linked controls as covered in the gap analysis.
      </p>
      <div className="tblwrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: '34%' }}>Policy</th>
              <th>Satisfies controls</th>
              <th>Status</th>
              <th>Customization</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => {
              const has = p.body.length > 0;
              const need = p.tokens.filter((t) => t.type !== 'auto');
              const filled = need.filter((t) => valOf(p.id, t).trim()).length;
              const st = statusOf(p);
              return (
                <tr
                  key={p.id}
                  className={has ? 'clk' : ''}
                  onClick={has ? () => setOpenId(p.id) : undefined}
                >
                  <td>
                    <div className="cname">{p.name}</div>
                    <div className="cdesc">{p.summary}</div>
                  </td>
                  <td>
                    <ControlChips codes={p.controls} />
                  </td>
                  <td>
                    <span className={`pst pst-${st}`}>{STATUS_LABEL[st]}</span>
                  </td>
                  <td>
                    <span className="cad">
                      {has ? `${filled} / ${need.length} fields` : 'None'}
                    </span>
                  </td>
                  <td>
                    {has ? (
                      <span className="plink">Customize →</span>
                    ) : (
                      <span style={{ color: 'var(--ink-45)', fontSize: '.76rem' }}>
                        Upload pending
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PolicyEditor({
  policy,
  valOf,
  onChange,
  onBack,
  onAdopt,
}: {
  policy: PolicyDoc;
  valOf: (t: PolicyToken) => string;
  onChange: (tokenId: string, value: string) => void;
  onBack: () => void;
  onAdopt: () => void;
}) {
  const need = policy.tokens.filter((t) => t.type !== 'auto');
  const filled = need.filter((t) => valOf(t).trim()).length;
  const fieldId = (tid: string) => `pf-${policy.id}-${tid}`;

  const renderParagraph = (text: string) => {
    const parts = text.split(/(\{\{\w+\}\})/g);
    return parts.map((part, i) => {
      const m = part.match(/^\{\{(\w+)\}\}$/);
      if (!m) return <Fragment key={i}>{part}</Fragment>;
      const t = policy.tokens.find((x) => x.id === m[1]);
      if (!t) return <Fragment key={i}>{part}</Fragment>;
      const v = valOf(t).trim();
      if (t.type === 'auto') {
        return (
          <span key={i} className="tok tok-auto" title="Auto-filled from profile">
            {valOf(t)}
          </span>
        );
      }
      const cls = t.type === 'choice' ? 'tok-choice' : 'tok-input';
      const placeholder =
        t.type === 'choice' ? `[choose: ${t.label}]` : `[${t.label}]`;
      return (
        <span
          key={i}
          className={`tok ${cls}${v ? '' : ' empty'}`}
          onClick={() => document.getElementById(fieldId(t.id))?.focus()}
        >
          {v || placeholder}
        </span>
      );
    });
  };

  return (
    <section>
      <div className="editbar">
        <button className="btn btn-ghost" onClick={onBack}>
          ← Library
        </button>
        <div style={{ flex: 1 }}>
          <div className="cname" style={{ fontSize: '1rem' }}>
            {policy.name}
          </div>
          <div style={{ marginTop: '.2rem' }}>
            <ControlChips codes={policy.controls} />
          </div>
        </div>
        <button className="btn btn-grad" onClick={onAdopt}>
          Adopt policy
        </button>
      </div>

      <div className="legend">
        <span>
          <i className="legdot" style={{ background: 'rgba(123,63,228,.55)' }} /> Auto-filled
        </span>
        <span>
          <i className="legdot" style={{ background: '#eab308' }} /> Needs your input
        </span>
        <span>
          <i className="legdot" style={{ background: 'var(--g2)' }} /> Choose an option
        </span>
      </div>

      <div className="peditor">
        <div className="docpane">
          {policy.body.map((seg, i) =>
            'h' in seg ? (
              <h3 key={i}>{seg.h}</h3>
            ) : (
              <p key={i}>{renderParagraph(seg.p)}</p>
            ),
          )}
        </div>

        <div className="custpanel">
          <div className="cname" style={{ fontSize: '.9rem', marginBottom: '.15rem' }}>
            Customization points
          </div>
          <div
            className="mono"
            style={{ fontSize: 11, color: 'var(--ink-60)', marginBottom: '.85rem' }}
          >
            {filled} / {need.length} completed
          </div>
          <div>
            {policy.tokens.map((t) => {
              if (t.type === 'auto') {
                return (
                  <div key={t.id} className="cf">
                    <div className="cfl">
                      {t.label} <span className="tt tt-auto">auto</span>
                    </div>
                    <div className="cfv">{valOf(t)}</div>
                  </div>
                );
              }
              const badge =
                t.type === 'choice' ? (
                  <span className="tt tt-choice">choose</span>
                ) : (
                  <span className="tt tt-input">input</span>
                );
              return (
                <div key={t.id} className="cf">
                  <div className="cfl">
                    {t.label} {badge}
                  </div>
                  {t.type === 'choice' ? (
                    <select
                      id={fieldId(t.id)}
                      value={valOf(t)}
                      onChange={(e) => onChange(t.id, e.target.value)}
                    >
                      {!valOf(t) && <option value="">Choose…</option>}
                      {(t.options || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={fieldId(t.id)}
                      value={valOf(t)}
                      placeholder="Type a value"
                      onChange={(e) => onChange(t.id, e.target.value)}
                    />
                  )}
                  {t.guidance && <div className="cfg">{t.guidance}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
